import React, { useState } from 'react';
import axios from 'axios';
import imageCompression from 'browser-image-compression';
import { getApiBaseUrl } from '../../config/api';
import { compressVideo, formatBytes } from '../../utils/compressVideo';
import UploadProgressBar from '../shared/UploadProgressBar';
import { hydrateMentionsFromText, toStoredMentionText } from '../../utils/mentions';

const API_URL = getApiBaseUrl();

const resolveMediaUrl = (url) => {
  if (!url) return '';
  return /^https?:\/\//i.test(url)
    ? url
    : `${API_URL}/${String(url).replace(/^\/+/, '')}`;
};

export default function EditGroupPostForm({ post, user, onSuccess, onCancel }) {
  const safePost = post ?? {};
  const [title, setTitle] = useState(safePost.title ?? '');
  const [content, setContent] = useState(
    hydrateMentionsFromText((safePost.content ?? '').trim())
  );
  const [category, setCategory] = useState(safePost.category ?? 'General');
  const [country, setCountry] = useState(safePost.country ?? user?.country ?? '');
  const [loading, setLoading] = useState(false);
  const [keepMedia, setKeepMedia] = useState(
    Array.isArray(safePost.media)
      ? safePost.media.map((m) => ({
          ...m,
          url: resolveMediaUrl(m.url || m.media_url),
          type: m.type || m.media_type || 'image',
          keep: true,
        }))
      : []
  );
  const [newFiles, setNewFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);

  const addFiles = (files) => setNewFiles((prev) => [...prev, ...files]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!user?.id) {
      alert('Please log in to edit this post.');
      return;
    }

    setLoading(true);
    setUploadProgress(0);
    setSubmitStatus('Preparing…');
    try {
      const formData = new FormData();
      formData.append('post_id', safePost.id);
      formData.append('user_id', user.id);
      formData.append('title', title.trim());
      formData.append('content', toStoredMentionText(content.trim()));
      formData.append('category', category);
      formData.append('country', country);
      formData.append(
        'keep_media',
        JSON.stringify(keepMedia.filter((m) => m.keep).map((m) => m.url))
      );

      const totalMedia = newFiles.length;
      for (let i = 0; i < newFiles.length; i++) {
        const f = newFiles[i];
        const prepPct = Math.round(((i + 0.5) / Math.max(totalMedia, 1)) * 40);

        if (f.type.startsWith('image/')) {
          try {
            setSubmitStatus(
              totalMedia > 1
                ? `Compressing photo ${i + 1} of ${totalMedia}…`
                : 'Compressing photo…'
            );
            setUploadProgress(prepPct);
            const img = await imageCompression(f, {
              maxSizeMB: 1.5,
              maxWidthOrHeight: 1920,
              useWebWorker: true,
              onProgress: (pct) => {
                setSubmitStatus(
                  totalMedia > 1
                    ? `Compressing photo ${i + 1} of ${totalMedia}… ${pct}%`
                    : `Compressing photo… ${pct}%`
                );
                setUploadProgress(
                  Math.min(
                    39,
                    prepPct + Math.round((pct / 100) * (40 / Math.max(totalMedia, 1)))
                  )
                );
              },
            });
            formData.append('media[]', img, img.name);
          } catch {
            formData.append('media[]', f);
          }
        } else if (f.type.startsWith('video/')) {
          try {
            const originalSize = formatBytes(f.size);
            setSubmitStatus(
              totalMedia > 1
                ? `Compressing video ${i + 1} of ${totalMedia} (${originalSize})…`
                : `Compressing video (${originalSize})…`
            );
            setUploadProgress(prepPct);
            const result = await compressVideo(f, {
              onProgress: (pct) => {
                setSubmitStatus(
                  totalMedia > 1
                    ? `Compressing video ${i + 1} of ${totalMedia}… ${pct}%`
                    : `Compressing video… ${pct}%`
                );
                setUploadProgress(
                  Math.min(
                    39,
                    prepPct + Math.round((pct / 100) * (40 / Math.max(totalMedia, 1)))
                  )
                );
              },
            });
            const compressedVideo = result.file || result;
            if (result.mode === 'server') {
              setSubmitStatus(
                result.message ||
                  `Uploading ${originalSize} — server will convert…`
              );
            } else if (result.mode === 'compressed') {
              setSubmitStatus(
                `Compressed ${originalSize} → ${formatBytes(compressedVideo.size)}`
              );
            }
            formData.append('media[]', compressedVideo, compressedVideo.name);
          } catch (err) {
            console.warn('Video prep failed:', err);
            throw new Error(
              err?.message ||
                `Could not prepare "${f.name}" for upload. Use a smaller H.264 MP4 (under 80MB).`
            );
          }
        }
      }

      setSubmitStatus(totalMedia > 0 ? 'Uploading media…' : 'Saving…');
      setUploadProgress(totalMedia > 0 ? 40 : 10);

      const { data } = await axios.post(`${API_URL}/update_group_post.php`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
        timeout: 0,
        onUploadProgress: (event) => {
          const total = event.total || event.loaded;
          if (!total) return;
          const ratio = Math.min(1, event.loaded / total);
          setUploadProgress(Math.min(100, 40 + Math.round(ratio * 60)));
          const pct = Math.round(ratio * 100);
          setSubmitStatus(
            totalMedia > 0 ? `Uploading media… ${pct}%` : `Saving… ${pct}%`
          );
        },
      });

      setUploadProgress(100);
      setSubmitStatus('Finishing…');

      if (data.success) {
        onSuccess?.();
      } else {
        alert(data.message || 'Update failed');
      }
    } catch (err) {
      console.error(err);
      alert('Error: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
      setSubmitStatus('');
      setUploadProgress(0);
    }
  };

  if (!post) {
    return <p style={{ padding: 20 }}>Select a post to edit.</p>;
  }

  return (
    <form onSubmit={handleSave} style={styles.wrapper}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ margin: 0 }}>Edit Group Post</h3>
        {onCancel && (
          <button type="button" onClick={onCancel} style={styles.cancelBtn}>
            ✕
          </button>
        )}
      </div>

      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Title"
        required
        style={styles.input}
      />

      <textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        placeholder="What's on your mind?"
        required
        style={{ ...styles.input, minHeight: 100 }}
      />

      <select value={category} onChange={(e) => setCategory(e.target.value)} style={styles.input}>
        {['General', 'Really Lesson', 'COOL', 'ReallyAvatar', 'Coursera', 'TechTree'].map((c) => (
          <option key={c}>{c}</option>
        ))}
      </select>

      <input
        value={country}
        onChange={(e) => setCountry(e.target.value)}
        placeholder="Country"
        style={styles.input}
      />

      {keepMedia.length > 0 && (
        <div style={{ marginBottom: 12, display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {keepMedia.map((m, i) => (
            <div key={i} style={styles.mediaItem}>
              {String(m.type).startsWith('image') || /\.(jpe?g|png|gif|webp)$/i.test(m.url) ? (
                <img src={m.url} alt="" style={styles.thumb} />
              ) : (
                <video src={m.url} style={styles.thumb} />
              )}
              <label style={{ fontSize: 12 }}>
                <input
                  type="checkbox"
                  checked={m.keep}
                  onChange={() =>
                    setKeepMedia((prev) =>
                      prev.map((x, idx) => (idx === i ? { ...x, keep: !x.keep } : x))
                    )
                  }
                />{' '}
                keep
              </label>
            </div>
          ))}
        </div>
      )}

      <div
        onClick={() => document.getElementById('editGroupMediaInput').click()}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragging(false);
          addFiles(Array.from(e.dataTransfer.files));
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        style={{
          border: isDragging ? '2px dashed #0077b6' : '2px dashed #ccc',
          padding: 20,
          marginBottom: 12,
          textAlign: 'center',
          background: isDragging ? '#e0f7fa' : '#f9f9f9',
          cursor: 'pointer',
        }}
      >
        Drag & drop media or <strong>click to add</strong>
        <input
          id="editGroupMediaInput"
          type="file"
          accept="image/*,video/*"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => addFiles(Array.from(e.target.files))}
        />
      </div>

      {newFiles.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
          {newFiles.map((f, idx) => (
            <div key={idx} style={styles.mediaItem}>
              <button
                type="button"
                onClick={() => setNewFiles((prev) => prev.filter((_, i) => i !== idx))}
                style={styles.removeBtn}
              >
                ✕
              </button>
              {f.type.startsWith('image/') ? (
                <img src={URL.createObjectURL(f)} alt="" style={styles.thumb} />
              ) : (
                <video src={URL.createObjectURL(f)} style={styles.thumb} />
              )}
            </div>
          ))}
        </div>
      )}

      {loading && (
        <UploadProgressBar status={submitStatus} progress={uploadProgress} />
      )}

      <button type="submit" disabled={loading} style={styles.saveBtn}>
        {loading
          ? uploadProgress > 0
            ? `Uploading ${uploadProgress}%`
            : submitStatus || 'Saving…'
          : 'Save Changes'}
      </button>
    </form>
  );
}

const styles = {
  wrapper: {
    maxWidth: 600,
    background: '#fff',
    padding: 20,
    borderRadius: 8,
  },
  input: {
    width: '100%',
    padding: 10,
    fontSize: 16,
    borderRadius: 5,
    border: '1px solid #ccc',
    marginBottom: 12,
    boxSizing: 'border-box',
  },
  mediaItem: { position: 'relative' },
  thumb: {
    width: 100,
    height: 100,
    objectFit: 'cover',
    borderRadius: 6,
  },
  removeBtn: {
    position: 'absolute',
    top: -6,
    right: -6,
    width: 20,
    height: 20,
    borderRadius: '50%',
    background: '#dc2626',
    color: '#fff',
    border: 'none',
    cursor: 'pointer',
    fontSize: 12,
    lineHeight: '20px',
  },
  saveBtn: {
    background: '#0091c2',
    color: '#fff',
    border: 'none',
    padding: '10px 20px',
    borderRadius: 5,
    width: '100%',
    fontSize: 16,
    cursor: 'pointer',
  },
  cancelBtn: {
    background: 'none',
    border: 'none',
    fontSize: 20,
    cursor: 'pointer',
    color: '#666',
  },
};
